import React, { useState } from "react";
import api from "../lib/api";

const TIME_WINDOWS = [
  "10:00 AM – 11:30 AM",
  "11:30 AM – 1:30 PM",
  "5:00 PM – 6:30 PM",
  "6:30 PM – 8:30 PM",
];

const DAY_OPTIONS = [
  "Today",
  "Tomorrow",
  "This week — any weekday",
  "This Saturday",
  "Next week",
  "Sunday (by prior appointment)",
];

const CONCERN_OPTIONS = [
  "Consultation / check-up",
  "Tooth pain or sensitivity",
  "Cleaning / scaling",
  "Filling",
  "Root canal",
  "Crown / cap / bridge",
  "Denture",
  "Dental implant",
  "Full-mouth rehabilitation",
  "Braces / aligners",
  "Whitening / smile design",
  "Extraction / wisdom tooth",
  "Child dental care",
  "Something else",
];

export default function AppointmentForm({ sessionId, language, onSuccess, onCancel }) {
  const [form, setForm] = useState({
    patient_name: "",
    phone: "",
    concern: "",
    preferred_day: "",
    preferred_time: "",
    is_for_someone_else: false,
    other_person_name: "",
    other_person_age: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.patient_name.trim()) e.patient_name = "Please enter your name";
    const digits = (form.phone || "").replace(/\D/g, "").slice(-10);
    if (!/^[6-9]\d{9}$/.test(digits)) e.phone = "Enter a valid 10-digit Indian mobile";
    if (!form.concern) e.concern = "Please choose a concern";
    if (!form.preferred_day) e.preferred_day = "Please choose a preferred day";
    if (!form.preferred_time) e.preferred_time = "Please choose a time window";
    if (form.is_for_someone_else) {
      if (!form.other_person_name.trim()) e.other_person_name = "Their name";
      if (!form.other_person_age.trim()) e.other_person_age = "Their age";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        session_id: sessionId,
        patient_name: form.patient_name.trim(),
        phone: (form.phone || "").replace(/\D/g, "").slice(-10),
        concern: form.concern,
        preferred_day: form.preferred_day,
        preferred_time: form.preferred_time,
        is_for_someone_else: form.is_for_someone_else,
        other_person_name: form.is_for_someone_else ? form.other_person_name.trim() : null,
        other_person_age: form.is_for_someone_else ? form.other_person_age.trim() : null,
        language: language || "english",
      };
      await api.createAppointment(payload);
      onSuccess(payload);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setErrors({ _form: typeof detail === "string" ? detail : "Could not submit — please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sd-form-card" data-testid="appointment-form">
      <div className="sd-form-title">Book a consultation</div>
      <div className="sd-form-desc">
        Share these details and our team will confirm your slot on your number shortly.
        Nothing is charged now — booking is a request, not a payment.
      </div>

      <div className="sd-form-row">
        <label className="sd-label">Your name</label>
        <input
          data-testid="appt-name"
          className="sd-form-input"
          value={form.patient_name}
          onChange={(e) => update("patient_name", e.target.value)}
          placeholder="e.g. Rohan Sharma"
        />
        {errors.patient_name && <div className="sd-form-error">{errors.patient_name}</div>}
      </div>

      <div className="sd-form-row">
        <label className="sd-label">Mobile number (10 digits)</label>
        <input
          data-testid="appt-phone"
          className="sd-form-input"
          value={form.phone}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="9321636840"
          inputMode="tel"
          maxLength={15}
        />
        {errors.phone && <div className="sd-form-error">{errors.phone}</div>}
      </div>

      <div className="sd-form-row">
        <label className="sd-label">Concern / treatment interest</label>
        <select
          data-testid="appt-concern"
          className="sd-form-select"
          value={form.concern}
          onChange={(e) => update("concern", e.target.value)}
        >
          <option value="">Select…</option>
          {CONCERN_OPTIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {errors.concern && <div className="sd-form-error">{errors.concern}</div>}
      </div>

      <div className="sd-form-row">
        <label className="sd-label">Preferred day</label>
        <select
          data-testid="appt-day"
          className="sd-form-select"
          value={form.preferred_day}
          onChange={(e) => update("preferred_day", e.target.value)}
        >
          <option value="">Select…</option>
          {DAY_OPTIONS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {errors.preferred_day && <div className="sd-form-error">{errors.preferred_day}</div>}
      </div>

      <div className="sd-form-row">
        <label className="sd-label">Preferred time window</label>
        <select
          data-testid="appt-time"
          className="sd-form-select"
          value={form.preferred_time}
          onChange={(e) => update("preferred_time", e.target.value)}
        >
          <option value="">Select…</option>
          {TIME_WINDOWS.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        {errors.preferred_time && <div className="sd-form-error">{errors.preferred_time}</div>}
      </div>

      <div className="sd-check-row" style={{ marginBottom: 10 }}>
        <input
          id="sd-for-else"
          data-testid="appt-for-else"
          type="checkbox"
          checked={form.is_for_someone_else}
          onChange={(e) => update("is_for_someone_else", e.target.checked)}
        />
        <label htmlFor="sd-for-else">Booking for someone else</label>
      </div>

      {form.is_for_someone_else && (
        <>
          <div className="sd-form-row">
            <label className="sd-label">Their name</label>
            <input
              data-testid="appt-other-name"
              className="sd-form-input"
              value={form.other_person_name}
              onChange={(e) => update("other_person_name", e.target.value)}
            />
            {errors.other_person_name && <div className="sd-form-error">{errors.other_person_name}</div>}
          </div>
          <div className="sd-form-row">
            <label className="sd-label">Their age</label>
            <input
              data-testid="appt-other-age"
              className="sd-form-input"
              value={form.other_person_age}
              onChange={(e) => update("other_person_age", e.target.value)}
              placeholder="e.g. 62"
              inputMode="numeric"
              maxLength={3}
            />
            {errors.other_person_age && <div className="sd-form-error">{errors.other_person_age}</div>}
          </div>
        </>
      )}

      {errors._form && <div className="sd-form-error" style={{ marginBottom: 8 }}>{errors._form}</div>}

      <div className="sd-form-actions">
        <button
          data-testid="appt-submit"
          type="button"
          className="sd-btn-primary"
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? "Sending…" : "Request appointment"}
        </button>
        <button
          data-testid="appt-cancel"
          type="button"
          className="sd-btn-ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
